import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Card,
  Nav,
  Tab,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import usersService from "../../features/users/usersService";
import companiesService from "../../features/companies/companiesService";
import aiChatbotsService from "../../features/ai_chatbots/aiChatbotsService";
import { User, UserCompany } from "../../features/users/usersSlice";
import { Company } from "../../features/companies/companiesService";
import { AIChatbot } from "../../features/ai_chatbots/aiChatbotsService";
import { useTranslation } from "react-i18next";

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("general");
  const [companyDetails, setCompanyDetails] = useState<Record<number, Company | null>>({});
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [chatbotsByCompany, setChatbotsByCompany] = useState<
    Record<number, AIChatbot[]>
  >({});
  const [chatbotsLoading, setChatbotsLoading] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        setIsLoading(true);
        setError(null);
        const data = await usersService.getUserById(Number(userId));
        setUser(data);
      } catch (err) {
        console.error("Error fetching user:", err);
        setError(
          (err as any)?.response?.data?.detail || "Failed to load user profile."
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  useEffect(() => {
    if (!user?.companies?.length) {
      setCompanyDetails({});
      return;
    }
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      const result: Record<number, Company | null> = {};
      try {
        await Promise.all(
          user.companies.map(async (uc: UserCompany) => {
            try {
              const company = await companiesService.getCompany(uc.company_id);
              result[uc.company_id] = company;
            } catch {
              result[uc.company_id] = null;
            }
          })
        );
        setCompanyDetails(result);
      } catch (err) {
        console.error("Error fetching company details:", err);
      } finally {
        setCompaniesLoading(false);
      }
    };
    fetchCompanies();
  }, [user?.id, user?.companies]);

  useEffect(() => {
    if (!user?.companies?.length) {
      setChatbotsByCompany({});
      return;
    }
    const fetchChatbots = async () => {
      setChatbotsLoading(true);
      const result: Record<number, AIChatbot[]> = {};
      try {
        await Promise.all(
          user.companies.map(async (uc: UserCompany) => {
            const res = await aiChatbotsService.getChatbots({
              company_id: uc.company_id,
              limit: 100,
            });
            // Backend returns paginated { data, pagination }; some code paths use { chatbots, total }
            const list = res?.data ?? res?.chatbots ?? (Array.isArray(res) ? res : []);
            result[uc.company_id] = Array.isArray(list) ? list : [];
          })
        );
        setChatbotsByCompany(result);
      } catch (err) {
        console.error("Error fetching chatbots:", err);
      } finally {
        setChatbotsLoading(false);
      }
    };
    fetchChatbots();
  }, [user?.id, user?.companies]);

  if (isLoading) {
    return (
      <Container className="user-profile-page py-5">
        <div className="user-profile-loading py-5">
          <Spinner animation="border" variant="primary" />
          <span>Loading user profile...</span>
        </div>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container className="user-profile-page py-5">
        <Alert variant="danger" className="user-profile-error-card">
          {error || "User not found."}
        </Alert>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => navigate("/users")}
        >
          {t("back_to_users") || "Back to users"}
        </button>
      </Container>
    );
  }

  const initials = user.full_name
    ?.trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  return (
    <Container className="user-profile-page py-4">
      <div className="user-profile-header">
        <button
          type="button"
          className="user-profile-back"
          onClick={() => navigate("/users")}
          title={t("back_to_users") || "Back to users"}
        >
          <i className="bi bi-arrow-left" />
        </button>
        <div className="user-profile-avatar">{initials}</div>
        <div className="user-profile-identity">
          <h1 className="user-profile-name">{user.full_name}</h1>
          <p className="user-profile-meta">
            {user.username && (
              <span>
                <i className="bi bi-at" />
                {user.username}
              </span>
            )}
            <span>
              <i className="bi bi-envelope" />
              {user.email}
            </span>
          </p>
        </div>
      </div>

      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "general")}
      >
        <Card className="user-profile-card">
          <Nav variant="tabs" className="user-profile-tabs">
            <Nav.Item>
              <Nav.Link eventKey="general">
                <i className="bi bi-person" />
                {t("general") || "General"}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="company">
                <i className="bi bi-briefcase" />
                {t("company") || "Company"}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="ai-chatbots">
                <i className="bi bi-robot" />
                {t("ai_chatbots") || "AI Agents"}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content className="user-profile-tab-content">
            <Tab.Pane eventKey="general">
              <div className="user-profile-info-list">
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">
                    {t("full_name") || "Full name"}
                  </span>
                  <span className="user-profile-info-value">{user.full_name}</span>
                </div>
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">
                    {t("email") || "Email"}
                  </span>
                  <span className="user-profile-info-value">{user.email}</span>
                </div>
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">
                    {t("username") || "Username"}
                  </span>
                  <span className="user-profile-info-value">
                    {user.username || "—"}
                  </span>
                </div>
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">
                    {t("status") || "Status"}
                  </span>
                  <span className="user-profile-info-value">
                    <Badge bg={user.status === "active" ? "success" : "secondary"}>
                      {user.status}
                    </Badge>
                  </span>
                </div>
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">
                    {t("roles") || "Roles"}
                  </span>
                  <div className="user-profile-info-value user-profile-roles">
                    {user.roles?.length
                      ? user.roles.map((role) => (
                          <Badge key={role} bg="primary">
                            {role}
                          </Badge>
                        ))
                      : "—"}
                  </div>
                </div>
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">
                    {t("created_at") || "Created at"}
                  </span>
                  <span className="user-profile-info-value">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleString()
                      : "—"}
                  </span>
                </div>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="company">
              {companiesLoading ? (
                <div className="user-profile-loading">
                  <Spinner animation="border" size="sm" />
                  <span>Loading company details...</span>
                </div>
              ) : !user.companies?.length ? (
                <div className="user-profile-empty">
                  {t("no_company_linked") || "Şirkete bağlı değil."}
                </div>
              ) : (
                <div className="user-profile-companies">
                  {user.companies.map((uc) => {
                    const company = companyDetails[uc.company_id];
                    const name = company?.company_name ?? uc.company_name ?? `Company #${uc.company_id}`;
                    const isActive = company?.is_active ?? true;
                    return (
                      <Link
                        key={uc.company_id}
                        to={`/companies/${uc.company_id}/edit`}
                        className={`user-profile-company-card user-profile-card-link ${isActive ? "is-active" : "is-inactive"}`}
                      >
                        <div className="user-profile-company-card-inner">
                          <div className="user-profile-company-header">
                            <div className="user-profile-company-title-row">
                              <span className="user-profile-company-icon">
                                <i className="bi bi-briefcase" />
                              </span>
                              <h3 className="user-profile-company-name">{name}</h3>
                              <span className="user-profile-status-pill user-profile-status-pill-company">
                                {isActive ? (t("active") || "Active") : (t("inactive") || "Inactive")}
                              </span>
                            </div>
                            <div className="user-profile-company-header-right">
                              {uc.role && (
                                <span className="user-profile-role-tag">{uc.role}</span>
                              )}
                              <span className="user-profile-card-btn user-profile-card-btn-edit">
                                <i className="bi bi-pencil-square" />
                                {t("edit") || "Edit"}
                              </span>
                            </div>
                          </div>
                        {company && (
                          <>
                            <div className="user-profile-company-details">
                              {company.company_type && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("company_type") || "Type"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    {company.company_type}
                                  </span>
                                </div>
                              )}
                              {company.industry && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("industry") || "Industry"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    {company.industry}
                                  </span>
                                </div>
                              )}
                              {company.website && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("website") || "Website"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    <a
                                      href={
                                        company.website.startsWith("http")
                                          ? company.website
                                          : `https://${company.website}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {company.website}
                                    </a>
                                  </span>
                                </div>
                              )}
                              {company.email && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("email") || "Email"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    <a
                                      href={`mailto:${company.email}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {company.email}
                                    </a>
                                  </span>
                                </div>
                              )}
                              {company.phone && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("phone") || "Phone"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    {company.phone}
                                  </span>
                                </div>
                              )}
                              {company.company_size && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("company_size") || "Size"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    {company.company_size}
                                  </span>
                                </div>
                              )}
                              {(company.city || company.country) && (
                                <div className="user-profile-company-detail">
                                  <span className="user-profile-company-detail-label">
                                    {t("location") || "Location"}
                                  </span>
                                  <span className="user-profile-company-detail-value">
                                    {[company.city, company.country]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </span>
                                </div>
                              )}
                            </div>
                            {company.description && (
                              <div className="user-profile-company-description">
                                {company.description}
                              </div>
                            )}
                          </>
                        )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </Tab.Pane>

            <Tab.Pane eventKey="ai-chatbots">
              {chatbotsLoading ? (
                <div className="user-profile-loading">
                  <Spinner animation="border" size="sm" />
                  <span>Loading chatbots...</span>
                </div>
              ) : !user.companies?.length ? (
                <div className="user-profile-empty">
                  {t("no_company_linked") || "Şirkete bağlı değil."}
                </div>
              ) : (
                <>
                  {user.companies.map((uc) => {
                    const chatbots = chatbotsByCompany[uc.company_id] ?? [];
                    const companyName =
                      companyDetails[uc.company_id]?.company_name ??
                      uc.company_name ??
                      `Company #${uc.company_id}`;
                    return (
                      <div
                        key={uc.company_id}
                        className="user-profile-chatbots-section"
                      >
                        <h4 className="user-profile-chatbots-section-title">
                          {companyName}
                        </h4>
                        {!chatbots.length ? (
                          <div className="user-profile-empty">
{t("no_ai_chatbots_in_company") ||
                                "Bu şirkette AI ajanı yok."}
                          </div>
                        ) : (
                          <div className="user-profile-chatbots-list">
                            {chatbots.map((bot) => {
                              const botActive = bot.is_active ?? true;
                              return (
                                <Link
                                  key={bot.id}
                                  to={`/ai-chatbots/${bot.id}/edit`}
                                  className={`user-profile-chatbot-card user-profile-card-link ${botActive ? "is-active" : "is-inactive"}`}
                                >
                                  <div className="user-profile-chatbot-card-inner">
                                    <div className="user-profile-chatbot-header">
                                      <span className="user-profile-chatbot-icon">
                                        <i className="bi bi-robot" />
                                      </span>
                                      <div className="user-profile-chatbot-main">
                                        <h4 className="user-profile-chatbot-name">
                                          {bot.name}
                                        </h4>
                                        <div className="user-profile-chatbot-meta">
                                          <span>
                                            <i className="bi bi-cpu" />
                                            {bot.model || "—"}
                                          </span>
                                          {bot.created_at && (
                                            <span>
                                              <i className="bi bi-calendar3" />
                                              {new Date(bot.created_at).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <span className={`user-profile-status-pill user-profile-status-pill-chatbot ${botActive ? "is-active" : "is-inactive"}`}>
                                        <i className="bi bi-circle-fill" />
                                        {botActive ? (t("active") || "Active") : (t("inactive") || "Inactive")}
                                      </span>
                                      <span className="user-profile-card-btn user-profile-card-btn-edit">
                                        <i className="bi bi-pencil-square" />
                                        {t("edit") || "Edit"}
                                      </span>
                                    </div>
                                    {bot.description && (
                                      <p className="user-profile-chatbot-description">
                                        {bot.description}
                                      </p>
                                    )}
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Card>
      </Tab.Container>
    </Container>
  );
};

export default UserProfilePage;
